import type {
  ForwardRefExoticComponent,
  RefAttributes,
  ReactNode,
} from 'react';
import { forwardRef } from 'react';

import { CardBody } from './CardBody';
import { CardHeader } from './CardHeader';

import { Wrap, CloseButton } from './styles';
import { useStrings } from '@hooks/useStrings';

export interface CardProps {
  /**
   * Html id for the container.
   */
  id?: string;
  /**
   * Styles override.
   */
  className?: string;
  /**
   * Callback when the Card is dismissed.
   * Not specifying it will make the Card not dismissible
   */
  onDismiss?: () => void;
  /**
   * Children prop from React.
   */
  children: ReactNode;
}

export interface CompoundedCard
  extends ForwardRefExoticComponent<CardProps & RefAttributes<HTMLDivElement>> {
  Header: typeof CardHeader;
  Body: typeof CardBody;
}

/**
 * A container for content representing a single entity. e.g. a contact, article, or task.
 *
 * {@link https://zeroheight.com/76c439891/v/latest/p/463181-card Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-card--card Code documentation}
 *
 * @example
 * <Card />
 */
// ESLint gets confused by the TS type casting that's required to expose the Item
// eslint-disable-next-line react/display-name
export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, id, onDismiss, children }: CardProps, ref) => {
    const { actions } = useStrings();
    return (
      <Wrap className={className} id={id} ref={ref}>
        {onDismiss ? (
          <CloseButton description={actions.close} onClick={onDismiss} />
        ) : null}
        {children}
      </Wrap>
    );
  },
) as CompoundedCard;
Card.displayName = 'Card';

Card.Header = CardHeader;
Card.Body = CardBody;
